import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import MenuIcon from '@mui/icons-material/Menu';
import Link from '@mui/material/Link';
import isEmpty from 'lodash/isEmpty';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { resolvePath, useNavigate, useQuery, welcome } from '../routes';
import { fromLayout, fromOrganizations, fromSubscriptions, useAppDispatch } from '../store';
import { ReactComponent as KatalonHeaderIcon } from './icons/KatalonHeaderIcon.svg';
import OrganizationSettingMenu from './OrganizationSettingMenu';
import UserSettingMenu from './UserSettingMenu';
import ListOrganizationsAndProjects from './ListOrganizationsAndProjects';
import { accountHome } from './routes';
import { useLaunchDarkly } from '../launchdarkly';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    height: theme.spacing(7.5),
    color: theme.palette.getContrastText(theme.palette.background.paper),
    boxShadow: '0px 6px 9px rgba(113, 131, 175, 0.05)',
    display: 'flex',
    justifyContent: 'center',
    padding: '0 0 0 1.5rem',
  },
  grid: {
    height: '100%',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
  },
  controlSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0 .5rem',
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
      width: 0,
    },
  },
  settingSection: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginRight: theme.spacing(2),
  },
  testOpsIconWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const Header = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { replace, mergeQuery } = useNavigate();
  const orgId = useQuery().get('orgId');
  let accountId = useQuery().get('accountId') || '' as string;
  const organization = useSelector(fromOrganizations.selectSelectedOrganization);
  const { changeAccount } = useLaunchDarkly();
  if (!accountId) {
    accountId = organization ? organization.accountId?.toString() as string : '';
  }
  const organizationsFromAccount = useSelector(
    fromOrganizations.selectAllByAccountId(Number(accountId)),
  );

  const handleMenuClick = () => {
    dispatch(fromLayout.doChangeMenuVisibility(true));
  };

  useEffect(() => {
    if (orgId) {
      dispatch(fromOrganizations.doChangeSelectedId(+orgId));
      dispatch(fromSubscriptions.doGetActiveSubscriptionsByOrgId(
        { organizationId: +orgId },
      ));
    }
    if (Number(accountId) && changeAccount) {
      changeAccount(Number(accountId));
    }
    if (accountId && !orgId) {
      dispatch(fromOrganizations.doFetchOrganizationByAccountId({ accountId: Number(accountId) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, accountId, changeAccount]);

  useEffect(() => {
    if (!orgId && !isEmpty(organizationsFromAccount)) {
      replace(undefined, mergeQuery({ orgId: organizationsFromAccount[0].id }));
    }
  }, [orgId, organizationsFromAccount]);

  return (
    <AppBar className={classes.root} position="fixed" elevation={0}>
      <Grid classes={{ root: classes.grid }} container>
        <Grid item sm={10} md={9} lg={8} className={classes.logoSection}>
          <Link
            className={classes.testOpsIconWrapper}
            component={RouterLink}
            to={orgId ? resolvePath(accountHome, undefined, { orgId: `${orgId}`, accountId: `${accountId}` }) : resolvePath(welcome)}
          >
            <KatalonHeaderIcon />
          </Link>
          <ListOrganizationsAndProjects />
        </Grid>
        <Grid item sm={2} md={3} lg={4} className={classes.controlSection}>
          <Hidden smDown>
            <div className={classes.settingSection}>
              {orgId && organization && (<OrganizationSettingMenu />)}
              <UserSettingMenu organization={organization} />
            </div>
          </Hidden>
          <Hidden smUp>
            <IconButton color="primary" onClick={handleMenuClick} size="large">
              <MenuIcon />
            </IconButton>
          </Hidden>
        </Grid>
      </Grid>
    </AppBar>
  );
};

export default Header;
